import React, { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import PageHeader from "../components/PageHeader";
import { Trash2, Database, ShieldAlert, RefreshCcw } from "lucide-react";

const Settings = () => {
    const { user } = useAuth();
    const [busy, setBusy] = useState("");
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");

    const isSuper = user?.role === "super_admin";

    const wipe = async () => {
        if (!window.confirm("Permanently delete ALL customers, drivers, rides and tickets? Admins, fare rules and promo codes will be kept.")) return;
        setBusy("wipe");
        setErr("");
        setMsg("");
        try {
            const r = await api.post("/admin/wipe-demo-data");
            setMsg(`Wiped — customers: ${r.data.customers_deleted}, drivers: ${r.data.drivers_deleted}, rides: ${r.data.rides_deleted}, tickets: ${r.data.tickets_deleted}`);
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setBusy("");
        }
    };

    const seed = async () => {
        if (!window.confirm("Re-seed demo customers / drivers / rides / tickets? Existing data will not be removed.")) return;
        setBusy("seed");
        setErr("");
        setMsg("");
        try {
            await api.post("/admin/seed-demo-data");
            setMsg("Demo data seeded.");
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setBusy("");
        }
    };

    return (
        <div className="space-y-6 max-w-3xl">
            <PageHeader title="Settings" subtitle="System utilities (super admin only)" />

            {!isSuper && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900 flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4" /> These actions are restricted to super admins.
                </div>
            )}

            {msg && <div className="bg-mint-50 border border-mint-200 text-mint-800 rounded-xl px-4 py-3 text-sm" data-testid="settings-msg">{msg}</div>}
            {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm" data-testid="settings-err">{err}</div>}

            <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-1">
                    <Database className="w-4 h-4 text-mint-600" />
                    <div className="font-display font-semibold text-navy-900">Demo data</div>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                    Once your Customer App and Driver App are wired to this console, you can wipe the seeded demo data here.
                    Use re-seed to bring it back for testing.
                </p>
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={wipe}
                        disabled={!isSuper || busy === "wipe"}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-red-500 hover:bg-red-600 text-white text-sm font-semibold disabled:opacity-50"
                        data-testid="settings-wipe-btn"
                    >
                        <Trash2 className="w-4 h-4" />
                        {busy === "wipe" ? "Wiping…" : "Wipe all demo data"}
                    </button>
                    <button
                        onClick={seed}
                        disabled={!isSuper || busy === "seed"}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-200 hover:bg-slate-50 text-navy-900 text-sm font-semibold disabled:opacity-50"
                        data-testid="settings-seed-btn"
                    >
                        <RefreshCcw className="w-4 h-4" />
                        {busy === "seed" ? "Seeding…" : "Re-seed demo data"}
                    </button>
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <div className="font-display font-semibold text-navy-900 mb-2">Integration endpoints</div>
                <p className="text-sm text-slate-500 mb-4">Point your Customer / Driver apps at these endpoints to populate this console live:</p>
                <ul className="text-sm space-y-2 font-mono text-slate-700">
                    <li><span className="text-mint-700 font-semibold">POST</span> /api/public/customers/register</li>
                    <li><span className="text-mint-700 font-semibold">POST</span> /api/public/drivers/register</li>
                    <li><span className="text-mint-700 font-semibold">POST</span> /api/public/drivers/&lt;id&gt;/documents/upload <span className="text-slate-400">(multipart, field: type + file)</span></li>
                    <li><span className="text-mint-700 font-semibold">POST</span> /api/public/rides</li>
                    <li><span className="text-amber-700 font-semibold">PATCH</span> /api/public/rides/&lt;id&gt;</li>
                    <li><span className="text-mint-700 font-semibold">POST</span> /api/public/tickets</li>
                </ul>
                <p className="text-xs text-slate-400 mt-3">No auth required on /public endpoints — gate at the network or proxy layer in production.</p>
            </div>
        </div>
    );
};

export default Settings;
